import type { Culture } from '~/types/locale'
import type { filterParam } from '~/utils/shop'
import type { Pagination } from '~/types/utility'

/**
 * Main filter config, must match API endpoint keys & i18n keys
 * Edit here & ~/utils/shop
 */
export enum Filter {
  Colors = 'colors',
  Prices = 'prices',
  SalesAndSpecials = 'salesAndSpecials',
  ImperialSizes = 'imperialSizes',
  MetricSizes = 'metricSizes',
  ImperialNoteCardSizes = 'imperialNoteCardSizes',
  MetricNoteCardSizes = 'metricNoteCardSizes',
  ImperialEnvelopeSizes = 'imperialEnvelopeSizes',
  MetricEnvelopeSizes = 'metricEnvelopeSizes',
  Occasions = 'occasions',
  InventoryStatuses = 'inventoryStatuses',
  DiscountPercents = 'discountPercents',
  ColorCollections = 'colorCollections',
  Catalogs = 'catalogs',
  StampSetTypes = 'stampSetTypes',
  StampMaterials = 'stampMaterials',
  StampSetLanguages = 'stampSetLanguages',
  ArchivalQualities = 'archivalQualities',
  ClearBlockSizes = 'clearBlockSizes',
  Techniques = 'techniques',
  ProductSuites = 'productSuites',
  Seasons = 'seasons',
  EmbellishmentTypes = 'embellishmentTypes',
  AdhesiveTypes = 'adhesiveTypes',
  RibbonTypes = 'ribbonTypes',
  SpecialtyPaperTypes = 'specialtyPaperTypes',
  ReleaseTypes = 'releaseTypes',
  DesignStyles = 'designStyles'
}

export interface Suggestion {
  id?: string
  culture?: string
  title: string
  slug?: string
  images?: string[]
  route?: string
}

export interface SuggestionList {
  products: Suggestion[]
  content: Suggestion[]
}

/*
**  Sort
*/

/**
 * The data options for orderby param
 */
export enum CatalogProductsAPIOrderBy {
  Relevance = 'relevance',
  ItemPrice = 'itemPrice',
  ItemTitle = 'itemTitle',
  ItemDate = 'launchDate'
}

/*
**  Query Params
*/

/**
 * The options for query params
 *
 * @export
 * @enum {string}
 */
export enum ShopPageQueryParam {
  Pages = 'pages',
  SortBy = 'sort-by',
  SortDesc = 'sort-desc',
  Filter = 'filter',
  Filters = 'filters',
  ResetAllFilters = 'reset-all-filters'
}

/*
**  API Interfaces
*/
export interface SubscriptionProduct {
  itemId: string
  text: string
  value: string
  price: string
  rewards: string
}

export interface ProductOfTheMonthProduct {
  itemId: string
  title: string
  imageUrl: string
  slug: string
  canBePurchased: boolean
  language?: string
}

export interface ProductOfTheMonthData {
  month: string
  price: string
  defaultLanguage?: string
  products: ProductOfTheMonthProduct[]
}

export interface ProductOfTheMonthAPIResponse {
  products: ProductOfTheMonthData[]
}

/**
 * The data scheme for submitting request to Catalog API without filters
 */
export interface CatalogProductsAPIQueryParamsBase {
  page?: number
  pageSize?: number
  term?: string
  category?: string
  orderBy?: string
  viewAsDate?: string
}

/**
 * Content Page Result
 */
export interface PageResult {
  route: string
  title: string
  description?: string
}

/**
 * The data scheme for submitting request to Content API
 *
 * @export
 * @interface ContentAPIQueryParams
 */
export interface ContentAPIQueryParams {
  path: string
  culture: Culture
}

export interface ProductBase {
  id?: string
  productId?: string
  categorySlugs?: string[]
  itemPrice: number
  canBePurchased: boolean
  slug: string
  exclusiveTo?: string[]
  name?: string
  title?: string
  formattedItemPrice: string
}

export interface ProductAsset {
  type: 'image' | 'file' | 'video'
  title: string
  coverImage: string
  location: string
}

export interface Assets {
  images: ProductAsset[]
  files: ProductAsset[]
  videos: ProductAsset[]
}

export enum RelationshipType {
  COLLECTIONS = 'collections',
  BUNDLE = 'bundle',
  DIGITAL_CONTENT = 'digitalContent',
  COMPONENTS = 'components',
  DIRECT_COORDINATION = 'directCoordination'
}

// Panel display order for relationships
export const RELATIONSHIP_PANEL_ORDER = [
  RelationshipType.COLLECTIONS,
  RelationshipType.BUNDLE,
  RelationshipType.DIGITAL_CONTENT,
  RelationshipType.COMPONENTS,
  RelationshipType.DIRECT_COORDINATION
]

export interface ProductRelationships {
  [RelationshipType.COLLECTIONS]?: string[]
  [RelationshipType.BUNDLE]?: string[]
  [RelationshipType.DIGITAL_CONTENT]?: string[]
  [RelationshipType.COMPONENTS]?: string[]
  [RelationshipType.DIRECT_COORDINATION]?: string[]
}

export interface ProductAPIResponse extends ProductBase {
  beginSaleDate?: string
  categorySlugs: string[]
  color: string[]
  culture: string
  description: string
  endSaleDate?: string
  formattedOriginalItemPrice: string
  hasSalePrice: boolean
  id: string
  qtyLimit?: number
  images: string[]
  inventoryStatus?: string
  launchDate?: string
  originalItemPrice: number
  title: string
  hoverImage?: string
  metaDescription?: string
  isCommissionable: boolean
  excludeFrom: string[]
  languages: string[]
  qualifier: string[]
  lifeCycleStates?: string[]
  metaTitle?: string
  loyaltyEarnings?: number
  californiaProp65Warning?: string
  formattedLoyaltyEarnings?: string
  productOfTheMonth: boolean
  isNew: boolean
  assets: Assets
  relationships?: ProductRelationships
}

// The prose fields a listed product leaves out. toListedProduct strips exactly these, so the type
// and the runtime cannot drift apart.
export const LISTED_PRODUCT_OMITTED_FIELDS = ['description', 'metaDescription', 'metaTitle'] as const

/**
 * A product as the category and search lists keep it. They show none of the prose fields, and
 * the PDP fetches its own product; the store is serialised into the page payload, so leaving
 * them out keeps that text out of every category page.
 */
export type ListedProduct = Omit<ProductAPIResponse, (typeof LISTED_PRODUCT_OMITTED_FIELDS)[number]>

export interface Color2APIResponse {
  hex?: string
  image?: string
  sortOrder: number
}

export interface ColorAPIResponse {
  color: Color2APIResponse
  className?: string
  formattedValue?: unknown
  value: string
  count: number
  isSelected?: boolean
}

export interface FilterAPIResponse {
  formattedValue?: string
  value: string
  count: number
  isSelected: boolean
}

export interface OrderByAPIResponse {
  formattedValue: string
  value: string
  count: number
  isSelected: boolean
}

export enum InventoryStatus {
  backordered = 'backordered',
  notorderable = 'notorderable',
  discontinued = 'discontinued',
  normal = 'normal',
  lowinventory = 'lowinventory',
  retiring = 'retiring'
}

export enum FilterConfigLayout {
  Text = 'Text',
  ColorSwatches = 'ColorSwatches'
}

export enum UnitVisible {
  Imperial = 'imperial',
  Metric = 'metric'
}

export interface FilterConfig {
  layout?: FilterConfigLayout // default: Text
  order: number
  key?: string // default to filter name
  unitVisible?: UnitVisible
  toggle?: true
  hidden?: boolean
  unitToggle?: true
  toggleKey?: string
  toggleInverse?: true
}

export type LanguageVariant = Partial<ProductAPIResponse>

export type FiltersConfig = Record<Filter, FilterConfig>
export type FilterState = Record<Filter, boolean>
export type FilterValuesState = Record<Filter, string[]>

/**
 * The data scheme for the filter fields in response from Catalog API
 */
export type QueryProductsAPIResponseFilter = {
  totalPages: number
} & Record<Filter, ColorAPIResponse[] | FilterAPIResponse[]>

export type FilterValue = QueryProductsAPIResponseFilter[keyof QueryProductsAPIResponseFilter]
export type MapFilterValue = (key: Filter, value: FilterValue) => FilterValue
export type FilterParamKey = ReturnType<typeof filterParam>

/**
 * The data scheme the filter fields for submitting request to Catalog API
 */
export type CatalogProductsAPIQueryParamsFilter = Partial<Record<FilterParamKey, string[]>>

/**
 * The data scheme for submitting request to Catalog API
 */
export type CatalogProductsAPIQueryParams = CatalogProductsAPIQueryParamsBase & CatalogProductsAPIQueryParamsFilter

/**
 * The data scheme for the response from Catalog API without filters
 */
export interface QueryProductsAPIResponseBase extends Pagination {
  totalProducts: number
  pages: number
  products: ProductAPIResponse[]
  contentResults: PageResult[]
  orderBy: OrderByAPIResponse[]
}

/**
 * The data scheme for the response from Catalog API
 */
export type QueryProductsAPIResponse = QueryProductsAPIResponseBase & QueryProductsAPIResponseFilter

export interface SortingVariant {
  sort?: CatalogProductsAPIOrderBy,
  desc?: null, // null treated as true in vue-router
  i18nKey: 'dateDesc' | 'dateAsc' | 'relevance' | 'priceAsc' | 'priceDesc' | 'titleAsc' | 'titleDesc'
}

export interface ProductListPayload {
  queryParams: {
    term?: string
    category?: string
    orderBy: string
    viewAsDate?: string
    filterValues: FilterValuesState
    pages: number
  }
  products: ListedProduct[]
  allPage: number
  allResult: number
  allProduct: number
  contentPages: PageResult[]
  filterOptions: QueryProductsAPIResponseFilter
}

export interface UpdateQueryParamPayloadPages {
  filter: ShopPageQueryParam.Pages
  value: string
}
export interface UpdateQueryParamPayloadSortBy {
  filter: ShopPageQueryParam.SortBy
  value: CatalogProductsAPIOrderBy
}
export interface UpdateQueryParamPayloadSortDesc {
  filter: ShopPageQueryParam.SortDesc
  value: boolean
}
export interface UpdateQueryParamPayloadFilter {
  filter: ShopPageQueryParam.Filter
  value: [Filter, string[]]
}
export interface UpdateQueryParamPayloadFilters {
  filter: ShopPageQueryParam.Filters
  value: Record<Filter, string[]>
}
export interface UpdateQueryParamPayloadResetAllFilters {
  filter: ShopPageQueryParam.ResetAllFilters
}
export type UpdateQueryParamPayload =
  | UpdateQueryParamPayloadPages
  | UpdateQueryParamPayloadSortBy
  | UpdateQueryParamPayloadSortDesc
  | UpdateQueryParamPayloadFilter
  | UpdateQueryParamPayloadFilters
  | UpdateQueryParamPayloadResetAllFilters

export interface ProductMediaProps {
  images?: string[]
  alt?: string
  imagesLibrary?: string
  title?: string
  videoList?: string[]
}

export interface ProductMediaEmits {
  (e: 'image-selected' | 'image-change', index: number): void
  (e: 'image-click'): void
  (e: 'video-click', videoUrl: string): void
}

export interface MediaItem {
  image: string
  youtubeId?: string
  thumbnailSrc: string
}
